// Field placement maps for the ORIGINAL DCH GAPP appendix PDFs.
//
// The state forms are never re-typeset: the blank originals live in
// public/forms/gapp/ and gappOverlay.js draws typed values, X-marks and
// signatures onto them at the coordinates below. Coordinates are PDF points
// (origin bottom-left, US Letter 612 x 792). Each `y` is the baseline of the
// form's own underline row, taken from the PDF text stream; each `x` is where
// that underline starts. Which party signs where mirrors the SignWell
// "GAPP Unified Intake Packet" template (parent = client, agency staff = agency,
// RN = rn) so the CAERA output is a drop-in replacement for the SignWell one.
//
// Item kinds:
//   text  — typed value, Helvetica, shrunk to fit `w`
//   sig   — cursive signature of slot `slot` (drawn only once signed)
//   sdate — date the slot was signed (MM/DD/YYYY)
//   sname — printed name of the slot's signer (Helvetica)
//   mark  — "X" centred on x when `when(ctx)` is true
//   lines — one value wrapped across several underline rows
//
// `get(ctx)` receives { c: client, f: merged form fields, s(slotKey) }.
//
// Spanish: the official DCH Spanish forms live in public/forms/gapp/es/, split
// out of the state's own unified Spanish packet (docs/GAPP Appendices for
// signature/GAPP_Unified_Packet_Spanish_.pdf). An appendix with `esFile` is
// filled on the Spanish original using `esItems`; the Spanish layouts differ
// from the English ones, so the coordinates are separate. Appendices with no
// `esFile` — N, V and R — stay on the official English form in a Spanish
// packet, because that is the only version DCH publishes and accepts.
// Never re-typeset a state form in either language.
//
// The Spanish coordinates below were measured from the vector geometry of the
// state's file (the packet is outlined art with no text layer): every blank is
// a filled rule, and `y` is the top of that rule.

const AGENCY_NAME = 'A Heart For Care';

// Appendix N checklist rows, top to bottom, exactly as printed on the form.
// `legacy` is the label the builder used before the originals were adopted, so
// drafts saved under the old wording still tick the right box.
export const N_CHECKLIST_ITEMS = [
  { label: 'Physician Plan of Treatment - Appendix T', legacy: 'Physician Plan of Treatment (Appendix T)', y: 342.9 },
  { label: 'Letter of Understanding (yearly)', legacy: 'Letter of Understanding (yearly)', y: 330.3 },
  { label: 'Freedom of Choice Form (yearly)', legacy: 'Freedom of Choice Form (yearly)', y: 317.6 },
  { label: 'Current IEP (if there is one)', legacy: 'Current IEP', y: 305.0 },
  { label: 'Attestation for Transfers and Shared Hours - Appendix U', legacy: 'Attestation — Appendix U', y: 292.4 },
  { label: 'Caregiver Skills Checklist Teaching Plan for Skilled Nursing', legacy: 'Caregiver Skills Checklist / Teaching Plan', y: 279.6 },
  { label: 'Most recent 3 months of Narrative Nursing notes', legacy: 'Most recent 3 months Narrative Nursing notes', y: 267.1 },
  { label: 'Medical Necessity Statement for Admission or Continued Stay', legacy: 'Medical Necessity Statement (Appendix H)', y: 241.7 },
  { label: 'Seizure Logs (if requesting hours for seizure activity)', legacy: 'Seizure Logs (Appendix V)', y: 229.1 },
  { label: 'Physician Office Visit Notes', legacy: 'Physician Office Visit Notes', y: 216.4 },
  { label: '60 Day Summary', legacy: '60 Day Summary', y: 203.8 },
  { label: 'Nursing Care Plan', legacy: 'Nursing Care Plan', y: 191.1 },
  { label: 'Supervisory Visit Notes', legacy: 'Supervisory Visit Notes', y: 178.5 },
];

const yes = (v) => String(v || '').toLowerCase() === 'yes';
const no = (v) => String(v || '').toLowerCase() === 'no';
// A few state fields are free text that happens to hold Yes/No (Appendix H's
// IEP line). On the Spanish form it has to read in Spanish.
const esYesNo = (v) => (yes(v) ? 'Sí' : no(v) ? 'No' : (v || ''));

// ── Appendix N — Admission / Continued Stay Checklist (1 page) ───────────────
const appendix_n = {
  file: 'appendix_n.pdf',
  items: [
    { kind: 'text', page: 0, x: 142, y: 621.2, w: 382, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 152, y: 595.9, w: 392, get: ({ f }) => f.nAddress },
    { kind: 'text', page: 0, x: 196, y: 570.7, w: 348, get: ({ c }) => c.medicaid },
    { kind: 'text', page: 0, x: 140, y: 545.4, w: 388, get: ({ c }) => c.guardian },
    { kind: 'text', page: 0, x: 131, y: 482.1, w: 414, get: ({ f }) => f.nDfcsCounty },
    { kind: 'text', page: 0, x: 136, y: 469.4, w: 415, get: ({ f }) => f.nDfcsAddress },
    { kind: 'lines', page: 0, rows: [{ x: 333, y: 456.8, w: 211 }, { x: 56, y: 444.1, w: 492 }], get: ({ f }) => f.nDfcsSupervisor },
    { kind: 'text', page: 0, x: 62, y: 406.1, w: 485, get: ({ f }) => f.nPhysician },
    ...N_CHECKLIST_ITEMS.map((it) => ({
      kind: 'mark', page: 0, x: 70, y: it.y,
      when: ({ f }) => (f.nChecklist || []).includes(it.label) || (f.nChecklist || []).includes(it.legacy),
    })),
    { kind: 'text', page: 0, x: 176, y: 140.5, w: 376, get: () => AGENCY_NAME },
    { kind: 'text', page: 0, x: 206, y: 115.2, w: 348, get: ({ f }) => f.nAgencyRep },
    { kind: 'text', page: 0, x: 170, y: 89.9, w: 79, get: ({ f, packetDate }) => f.nPacketDate || packetDate },
    { kind: 'text', page: 0, x: 309, y: 89.9, w: 216, get: ({ f }) => f.nAdmission },
  ],
};

// ── Appendix I — Letter of Understanding (2 pages) ───────────────────────────
// Line 1: printed member name + caregiver signature; line 2: caregiver's own
// printed name + signature (same signer, as in SignWell); line 3: agency rep.
const appendix_i = {
  file: 'appendix_i.pdf',
  esFile: 'appendix_i.pdf',
  // Anexo I: member name on page 1; three full-width signature rules on page 2.
  esItems: [
    { kind: 'text', page: 0, x: 325, y: 623.5, w: 84, size: 9, get: ({ c }) => c.name },
    { kind: 'text', page: 1, x: 74, y: 544.1, w: 140, get: ({ c }) => c.name },
    { kind: 'sig', page: 1, x: 240, y: 544.1, w: 175, slot: 'i_caregiver' },
    { kind: 'sdate', page: 1, x: 470, y: 544.1, w: 68, slot: 'i_caregiver' },
    { kind: 'sname', page: 1, x: 74, y: 493.2, w: 140, slot: 'i_caregiver' },
    { kind: 'sig', page: 1, x: 240, y: 493.2, w: 175, slot: 'i_caregiver' },
    { kind: 'sdate', page: 1, x: 470, y: 493.2, w: 68, slot: 'i_caregiver' },
    { kind: 'sname', page: 1, x: 74, y: 455.2, w: 140, slot: 'i_agency' },
    { kind: 'sig', page: 1, x: 240, y: 455.2, w: 175, slot: 'i_agency' },
    { kind: 'sdate', page: 1, x: 470, y: 455.2, w: 68, slot: 'i_agency' },
  ],
  items: [
    { kind: 'text', page: 0, x: 291, y: 623.1, w: 84, size: 9, get: ({ c }) => c.name },
    { kind: 'text', page: 1, x: 74, y: 583.9, w: 138, get: ({ c }) => c.name },
    { kind: 'sig', page: 1, x: 220, y: 583.9, w: 170, slot: 'i_caregiver' },
    { kind: 'sdate', page: 1, x: 470, y: 583.9, w: 70, slot: 'i_caregiver' },
    { kind: 'sname', page: 1, x: 74, y: 533.4, w: 200, slot: 'i_caregiver' },
    { kind: 'sig', page: 1, x: 292, y: 533.4, w: 170, slot: 'i_caregiver' },
    { kind: 'sdate', page: 1, x: 470, y: 533.4, w: 70, slot: 'i_caregiver' },
    { kind: 'sname', page: 1, x: 74, y: 495.3, w: 205, slot: 'i_agency' },
    { kind: 'sig', page: 1, x: 292, y: 495.3, w: 170, slot: 'i_agency' },
    { kind: 'sdate', page: 1, x: 470, y: 495.3, w: 70, slot: 'i_agency' },
  ],
};

// ── Appendix G — Freedom of Choice (4 pages; pages 1 and 2 are signed) ───────
// The parent signs both the Member line (member is a minor) and the Authorized
// Representative line, exactly as the SignWell template places them. Page 2
// (Refusal) carries the same three signatures, as SignWell does — the caregiver
// is confirming they were informed of the right to refuse.
const appendix_g = {
  file: 'appendix_g.pdf',
  esFile: 'appendix_g.pdf',
  // Anexo G (Verificación + Aceptación), G-1 (Rechazo), G-2 (instrucciones).
  esItems: [
    { kind: 'sig', page: 0, x: 57, y: 402.4, w: 175, slot: 'g_provider' },
    { kind: 'sdate', page: 0, x: 381, y: 402.4, w: 90, slot: 'g_provider' },
    { kind: 'sig', page: 0, x: 57, y: 275.5, w: 180, slot: 'g_member' },
    { kind: 'sdate', page: 0, x: 381, y: 275.5, w: 94, slot: 'g_member' },
    { kind: 'sig', page: 0, x: 57, y: 237.4, w: 180, slot: 'g_caregiver' },
    { kind: 'sdate', page: 0, x: 381, y: 237.4, w: 94, slot: 'g_caregiver' },
    { kind: 'sig', page: 0, x: 57, y: 199.4, w: 180, slot: 'g_witness' },
    { kind: 'sdate', page: 0, x: 381, y: 199.4, w: 94, slot: 'g_witness' },
    { kind: 'sig', page: 1, x: 57, y: 478.1, w: 180, slot: 'g_member' },
    { kind: 'sdate', page: 1, x: 382, y: 478.1, w: 94, slot: 'g_member' },
    { kind: 'sig', page: 1, x: 57, y: 440.0, w: 180, slot: 'g_caregiver' },
    { kind: 'sdate', page: 1, x: 381, y: 440.0, w: 94, slot: 'g_caregiver' },
    { kind: 'sig', page: 1, x: 57, y: 402.0, w: 180, slot: 'g_witness' },
    { kind: 'sdate', page: 1, x: 381, y: 402.0, w: 94, slot: 'g_witness' },
  ],
  items: [
    { kind: 'sig', page: 0, x: 58, y: 420.8, w: 176, slot: 'g_provider' },
    { kind: 'sdate', page: 0, x: 381, y: 420.8, w: 90, slot: 'g_provider' },
    { kind: 'sig', page: 0, x: 58, y: 294.3, w: 180, slot: 'g_member' },
    { kind: 'sdate', page: 0, x: 381, y: 294.3, w: 94, slot: 'g_member' },
    { kind: 'sig', page: 0, x: 58, y: 256.4, w: 180, slot: 'g_caregiver' },
    { kind: 'sdate', page: 0, x: 381, y: 256.4, w: 94, slot: 'g_caregiver' },
    { kind: 'sig', page: 0, x: 58, y: 218.3, w: 180, slot: 'g_witness' },
    { kind: 'sdate', page: 0, x: 381, y: 218.3, w: 94, slot: 'g_witness' },
    { kind: 'sig', page: 1, x: 58, y: 540.0, w: 180, slot: 'g_member' },
    { kind: 'sdate', page: 1, x: 381, y: 540.0, w: 94, slot: 'g_member' },
    { kind: 'sig', page: 1, x: 58, y: 502.0, w: 180, slot: 'g_caregiver' },
    { kind: 'sdate', page: 1, x: 381, y: 502.0, w: 94, slot: 'g_caregiver' },
    { kind: 'sig', page: 1, x: 58, y: 464.1, w: 180, slot: 'g_witness' },
    { kind: 'sdate', page: 1, x: 381, y: 464.1, w: 94, slot: 'g_witness' },
  ],
};

// ── Appendix H — Medical Necessity Statement (2 pages; p2 = BHA variant) ─────
const appendix_h = {
  file: 'appendix_h.pdf',
  esFile: 'appendix_h.pdf',
  // Both files hold TWO state forms: Appendix H, then Appendix H-1 (Behavioral
  // Health Aide Services). H-1 is deliberately left blank — AHFC does not
  // provide that service, and the SignWell packet carries it blank too — so no
  // map below touches it.
  //
  // English fits H on page 1 and H-1 on page 2. Spanish is a longer language,
  // so the state's own layout runs to three: page 2 opens with H's signature
  // block and then starts H-1, which finishes on page 3. That is why a Spanish
  // packet is one page longer, and it is the state's typesetting, not ours.
  esItems: [
    { kind: 'text', page: 0, x: 208, y: 583.1, w: 275, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 286, y: 557.5, w: 99, get: ({ c }) => c.dob },
    { kind: 'text', page: 0, x: 133, y: 545.0, w: 110, get: ({ c }) => c.medicaid },
    { kind: 'mark', page: 0, x: 106, y: 519.6, when: ({ f }) => yes(f.hDfcsCustody) },
    { kind: 'mark', page: 0, x: 166, y: 519.6, when: ({ f }) => no(f.hDfcsCustody) },
    { kind: 'text', page: 0, x: 279, y: 519.6, w: 83, get: ({ f }) => f.hWeight },
    { kind: 'text', page: 0, x: 125, y: 481.6, w: 226, get: ({ f }) => f.hDfcsWorker },
    { kind: 'text', page: 0, x: 393, y: 405.6, w: 50, get: ({ f }) => f.hSkilledHrs },
    { kind: 'text', page: 0, x: 322, y: 392.9, w: 55, get: ({ f }) => f.hPssHrs },
    { kind: 'mark', page: 0, x: 100, y: 355.0, when: ({ f }) => yes(f.hOtherMedicaid) },
    { kind: 'mark', page: 0, x: 180, y: 355.0, when: ({ f }) => no(f.hOtherMedicaid) },
    { kind: 'text', page: 0, x: 243, y: 329.6, w: 292, get: ({ f }) => f.hServicesProvided },
    { kind: 'mark', page: 0, x: 398, y: 304.2, when: ({ f }) => yes(f.hFamilyPaid) },
    { kind: 'mark', page: 0, x: 450, y: 304.2, when: ({ f }) => no(f.hFamilyPaid) },
    { kind: 'mark', page: 0, x: 254, y: 279.0, when: ({ f }) => yes(f.hInSchool) },
    { kind: 'mark', page: 0, x: 313, y: 279.0, when: ({ f }) => no(f.hInSchool) },
    { kind: 'text', page: 0, x: 422, y: 279.0, w: 46, get: ({ f }) => f.hSchoolHrs },
    { kind: 'text', page: 0, x: 273, y: 266.3, w: 46, get: ({ f }) => esYesNo(f.hIep) },
    { kind: 'mark', page: 0, x: 440, y: 241.0, when: ({ f }) => yes(f.hNurseSchool) },
    { kind: 'mark', page: 0, x: 501, y: 241.0, when: ({ f }) => no(f.hNurseSchool) },
    { kind: 'text', page: 0, x: 74, y: 203.0, w: 189, size: 9, get: ({ f }) => f.hRationale },
    { kind: 'mark', page: 0, x: 266, y: 177.6, when: ({ f }) => yes(f.hPrivateIns) },
    { kind: 'mark', page: 0, x: 329, y: 177.6, when: ({ f }) => no(f.hPrivateIns) },
    { kind: 'mark', page: 0, x: 420, y: 152.3, when: ({ f }) => yes(f.hOtherGapp) },
    { kind: 'mark', page: 0, x: 484, y: 152.3, when: ({ f }) => no(f.hOtherGapp) },
    { kind: 'text', page: 0, x: 74, y: 114.4, w: 364, get: ({ f }) => f.hOtherGappNames },
    { kind: 'mark', page: 0, x: 437, y: 101.6, when: ({ f }) => yes(f.hCompetent) },
    { kind: 'mark', page: 0, x: 492, y: 101.6, when: ({ f }) => no(f.hCompetent) },
    { kind: 'text', page: 0, x: 74, y: 76.3, w: 448, get: ({ f }) => f.hPa },
    // Page 2: Anexo H signatures, then Anexo H-1 opens with the member's name.
    { kind: 'sig', page: 1, x: 220, y: 707.3, w: 193, size: 12, slot: 'h_caregiver' },
    { kind: 'sdate', page: 1, x: 456, y: 707.3, w: 79, slot: 'h_caregiver' },
    { kind: 'sname', page: 1, x: 149, y: 694.8, w: 353, slot: 'h_caregiver' },
    { kind: 'sig', page: 1, x: 242, y: 682.1, w: 177, size: 12, slot: 'h_agency' },
    { kind: 'sdate', page: 1, x: 458, y: 682.1, w: 79, slot: 'h_agency' },
    { kind: 'sname', page: 1, x: 149, y: 669.2, w: 386, slot: 'h_agency' },
    { kind: 'text', page: 1, x: 208, y: 499.6, w: 330, get: ({ c }) => c.name },
  ],
  items: [
    { kind: 'text', page: 0, x: 175, y: 585.6, w: 347, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 166, y: 560.3, w: 86, get: ({ c }) => c.dob },
    { kind: 'text', page: 0, x: 428, y: 560.3, w: 100, get: ({ c }) => c.medicaid },
    { kind: 'mark', page: 0, x: 257, y: 535.0, when: ({ f }) => yes(f.hDfcsCustody) },
    { kind: 'mark', page: 0, x: 317, y: 535.0, when: ({ f }) => no(f.hDfcsCustody) },
    { kind: 'text', page: 0, x: 428, y: 535.0, w: 96, get: ({ f }) => f.hWeight },
    { kind: 'text', page: 0, x: 265, y: 509.7, w: 242, get: ({ f }) => f.hDfcsWorker },
    { kind: 'text', page: 0, x: 273, y: 446.5, w: 50, get: ({ f }) => f.hSkilledHrs },
    { kind: 'text', page: 0, x: 478, y: 446.5, w: 55, get: ({ f }) => f.hPssHrs },
    { kind: 'mark', page: 0, x: 431, y: 421.1, when: ({ f }) => yes(f.hOtherMedicaid) },
    { kind: 'mark', page: 0, x: 503, y: 421.1, when: ({ f }) => no(f.hOtherMedicaid) },
    { kind: 'text', page: 0, x: 231, y: 395.8, w: 337, get: ({ f }) => f.hServicesProvided },
    { kind: 'mark', page: 0, x: 395, y: 370.5, when: ({ f }) => yes(f.hFamilyPaid) },
    { kind: 'mark', page: 0, x: 458, y: 370.5, when: ({ f }) => no(f.hFamilyPaid) },
    { kind: 'mark', page: 0, x: 210, y: 345.2, when: ({ f }) => yes(f.hInSchool) },
    { kind: 'mark', page: 0, x: 274, y: 345.2, when: ({ f }) => no(f.hInSchool) },
    { kind: 'text', page: 0, x: 371, y: 345.2, w: 46, get: ({ f }) => f.hSchoolHrs },
    { kind: 'text', page: 0, x: 452, y: 345.2, w: 46, get: ({ f }) => f.hIep },
    { kind: 'mark', page: 0, x: 413, y: 320.0, when: ({ f }) => yes(f.hNurseSchool) },
    { kind: 'mark', page: 0, x: 465, y: 320.0, when: ({ f }) => no(f.hNurseSchool) },
    { kind: 'text', page: 0, x: 74, y: 281.9, w: 190, size: 9, get: ({ f }) => f.hRationale },
    { kind: 'mark', page: 0, x: 283, y: 256.7, when: ({ f }) => yes(f.hPrivateIns) },
    { kind: 'mark', page: 0, x: 346, y: 256.7, when: ({ f }) => no(f.hPrivateIns) },
    { kind: 'mark', page: 0, x: 314, y: 231.4, when: ({ f }) => yes(f.hOtherGapp) },
    { kind: 'mark', page: 0, x: 377, y: 231.4, when: ({ f }) => no(f.hOtherGapp) },
    { kind: 'text', page: 0, x: 174, y: 206.1, w: 388, get: ({ f }) => f.hOtherGappNames },
    { kind: 'mark', page: 0, x: 362, y: 180.7, when: ({ f }) => yes(f.hCompetent) },
    { kind: 'mark', page: 0, x: 416, y: 180.7, when: ({ f }) => no(f.hCompetent) },
    { kind: 'text', page: 0, x: 74, y: 155.4, w: 454, get: ({ f }) => f.hPa },
    { kind: 'sig', page: 0, x: 224, y: 142.8, w: 198, size: 12, slot: 'h_caregiver' },
    { kind: 'sdate', page: 0, x: 459, y: 142.8, w: 78, slot: 'h_caregiver' },
    { kind: 'sname', page: 0, x: 143, y: 130.2, w: 400, slot: 'h_caregiver' },
    { kind: 'sig', page: 0, x: 212, y: 117.5, w: 208, size: 12, slot: 'h_agency' },
    { kind: 'sdate', page: 0, x: 458, y: 117.5, w: 78, slot: 'h_agency' },
    { kind: 'sname', page: 0, x: 143, y: 104.9, w: 400, slot: 'h_agency' },
    // Page 2 (Behavioral Health Aide statement) is carried through with only the
    // member's name, as the SignWell packet does.
    { kind: 'text', page: 1, x: 74, y: 596.3, w: 398, get: ({ c }) => c.name },
  ],
};

// ── Appendix U — Caregiver Attestation (1 page, no date lines) ───────────────
const appendix_u = {
  file: 'appendix_u.pdf',
  esFile: 'appendix_u.pdf',
  esItems: [
    { kind: 'sig', page: 0, x: 258, y: 495.4, w: 275, slot: 'u_caregiver' },
    { kind: 'sname', page: 0, x: 255, y: 457.3, w: 282, slot: 'u_caregiver' },
    { kind: 'sig', page: 0, x: 276, y: 419.3, w: 260, slot: 'u_agency' },
    { kind: 'sname', page: 0, x: 271, y: 381.2, w: 266, slot: 'u_agency' },
  ],
  items: [
    { kind: 'sig', page: 0, x: 258, y: 513.9, w: 274, slot: 'u_caregiver' },
    { kind: 'sname', page: 0, x: 255, y: 476.0, w: 280, slot: 'u_caregiver' },
    { kind: 'sig', page: 0, x: 255, y: 438.1, w: 280, slot: 'u_agency' },
    { kind: 'sname', page: 0, x: 255, y: 400.1, w: 280, slot: 'u_agency' },
  ],
};

// ── Appendix S — Shared Hours Request (1 page) ───────────────────────────────
const appendix_s = {
  file: 'appendix_s.pdf',
  esFile: 'appendix_s.pdf',
  esItems: [
    { kind: 'text', page: 0, x: 175, y: 547.7, w: 364, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 230, y: 522.5, w: 309, get: ({ c }) => c.dob },
    { kind: 'text', page: 0, x: 233, y: 496.9, w: 309, get: ({ c }) => c.medicaid },
    { kind: 'text', page: 0, x: 437, y: 471.7, w: 105, get: ({ f }) => f.sCurrentHrs },
    { kind: 'text', page: 0, x: 199, y: 446.3, w: 342, get: ({ f }) => f.sAgency1Name },
    { kind: 'text', page: 0, x: 270, y: 421.0, w: 232, get: ({ f }) => f.sAgency1Contact },
    { kind: 'text', page: 0, x: 210, y: 395.5, w: 325, get: ({ f }) => f.sAgency1Hrs },
    { kind: 'text', page: 0, x: 202, y: 370.3, w: 337, get: ({ f }) => f.sAgency2Name },
    { kind: 'text', page: 0, x: 270, y: 345.0, w: 270, get: ({ f }) => f.sAgency2Contact },
    { kind: 'text', page: 0, x: 210, y: 319.6, w: 325, get: ({ f }) => f.sAgency2Hrs },
    { kind: 'text', page: 0, x: 308, y: 294.4, w: 194, get: ({ f }) => f.sPaExp },
    { kind: 'text', page: 0, x: 259, y: 268.8, w: 276, get: ({ f }) => f.sSharedStart },
    { kind: 'sname', page: 0, x: 214, y: 243.6, w: 326, slot: 's_caregiver' },
    { kind: 'sig', page: 0, x: 170, y: 218.4, w: 370, slot: 's_caregiver' },
    { kind: 'text', page: 0, x: 227, y: 192.8, w: 314, get: ({ f, c }) => f.sCaregiverPhone || c.phone },
    { kind: 'sdate', page: 0, x: 107, y: 154.9, w: 84, slot: 's_caregiver' },
  ],
  items: [
    { kind: 'text', page: 0, x: 156, y: 573.0, w: 344, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 152, y: 547.7, w: 348, get: ({ c }) => c.dob },
    { kind: 'text', page: 0, x: 183, y: 509.7, w: 315, get: ({ c }) => c.medicaid },
    { kind: 'text', page: 0, x: 222, y: 484.4, w: 289, get: ({ f }) => f.sCurrentHrs },
    { kind: 'text', page: 0, x: 167, y: 459.1, w: 333, get: ({ f }) => f.sAgency1Name },
    { kind: 'text', page: 0, x: 203, y: 433.8, w: 313, get: ({ f }) => f.sAgency1Contact },
    { kind: 'text', page: 0, x: 208, y: 408.4, w: 291, get: ({ f }) => f.sAgency1Hrs },
    { kind: 'text', page: 0, x: 169, y: 383.2, w: 331, get: ({ f }) => f.sAgency2Name },
    { kind: 'text', page: 0, x: 203, y: 357.9, w: 296, get: ({ f }) => f.sAgency2Contact },
    { kind: 'text', page: 0, x: 208, y: 332.6, w: 309, get: ({ f }) => f.sAgency2Hrs },
    { kind: 'text', page: 0, x: 204, y: 307.2, w: 324, get: ({ f }) => f.sPaExp },
    { kind: 'text', page: 0, x: 189, y: 281.9, w: 339, get: ({ f }) => f.sSharedStart },
    { kind: 'sname', page: 0, x: 193, y: 256.7, w: 309, slot: 's_caregiver' },
    { kind: 'sig', page: 0, x: 183, y: 231.4, w: 345, slot: 's_caregiver' },
    { kind: 'text', page: 0, x: 201, y: 206.1, w: 321, get: ({ f, c }) => f.sCaregiverPhone || c.phone },
    { kind: 'sdate', page: 0, x: 103, y: 168.1, w: 89, slot: 's_caregiver' },
  ],
};

// ── Appendix V — Seizure Observation Log (1 page, 6 rows; repeats on overflow) ─
export const V_TABLE = {
  cols: [13.7, 119.1, 157.3, 218.9, 293.8, 358.4, 434.6, 557.6],
  rowTops: [553.1, 513.9, 483.4, 442.6, 401.8, 361.0],
  rowBottom: 319.7,
  keys: ['date', 'time', 'duration', 'type', 'intervention', 'response', 'reporter'],
};
const appendix_v = {
  file: 'appendix_v.pdf',
  items: [
    { kind: 'text', page: 0, x: 49, y: 646.5, w: 253, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 369, y: 646.5, w: 134, get: ({ c }) => c.age },
  ],
  table: { rowsKey: 'vSeizures', ...V_TABLE },
};

// ── Appendix R — Transfer Request (1 page) ───────────────────────────────────
const appendix_r = {
  file: 'appendix_r.pdf',
  items: [
    { kind: 'text', page: 0, x: 192, y: 616.2, w: 271, get: ({ f, packetDate }) => f.rTransferDate || packetDate },
    { kind: 'text', page: 0, x: 155, y: 591.0, w: 310, get: ({ c }) => c.name },
    { kind: 'text', page: 0, x: 158, y: 565.7, w: 310, get: ({ c }) => c.dob },
    { kind: 'text', page: 0, x: 183, y: 540.3, w: 282, get: ({ c }) => c.medicaid },
    { kind: 'text', page: 0, x: 186, y: 515.0, w: 282, get: ({ f }) => f.rCurrentAgency },
    { kind: 'text', page: 0, x: 201, y: 489.7, w: 272, get: ({ f }) => f.rCurrentContact },
    { kind: 'text', page: 0, x: 222, y: 464.5, w: 249, get: ({ f }) => f.rHrsApproved },
    { kind: 'text', page: 0, x: 204, y: 439.1, w: 271, get: ({ f }) => f.rPaExp },
    { kind: 'text', page: 0, x: 219, y: 413.8, w: 260, get: ({ f }) => f.rSubmissionDate },
    { kind: 'lines', page: 0, rows: [{ x: 169, y: 388.5, w: 309 }, { x: 74, y: 350.6, w: 403 }, { x: 74, y: 325.2, w: 403 }], get: ({ f }) => f.rReason },
    { kind: 'text', page: 0, x: 196, y: 299.9, w: 282, get: ({ f }) => f.rReceivingAgency },
    { kind: 'text', page: 0, x: 207, y: 274.6, w: 271, get: ({ f }) => f.rDma80Date },
    { kind: 'sname', page: 0, x: 193, y: 249.4, w: 287, slot: 'r_caregiver' },
    { kind: 'sig', page: 0, x: 183, y: 224.1, w: 298, slot: 'r_caregiver' },
    { kind: 'text', page: 0, x: 201, y: 198.7, w: 282, get: ({ f, c }) => f.rCaregiverPhone || c.phone },
    { kind: 'sdate', page: 0, x: 102, y: 173.4, w: 83, slot: 'r_caregiver' },
  ],
};

export const GAPP_FIELD_MAPS = {
  appendix_n, appendix_i, appendix_g, appendix_h, appendix_u, appendix_s, appendix_v, appendix_r,
};

// Forms rendered by our own @react-pdf templates (AHFC internal documents).
export const INTERNAL_FORMS = new Set(['phi_release', 'nacp_signature']);
